// Goal Date column. Income = Start + End; Wealth = single date; Retirement = a dropdown
// (Client/Co-Client Retirement->Planning / Custom) that derives or reveals Start + End.
import { useCallback, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { Box, useTheme } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks';
import { ComboSelect, FieldErrorTooltip, Typography } from '../../../components/atoms';
import {
    formatGoalDateDisplay,
    getRetirementDerivedDates,
    getTenureLockWindow,
    hasCoClient,
    isGoalTenureLocked,
    isPastStartDateLocked,
    rowHasPastGoalDate,
    selectIsPlanSavedOnce,
    GoalInlineRow,
    RetirementDateOption
} from '../goalInlineHelpers';
import { InlineDateField, GoalRowCtx, goalsReadTextSx, mutedPlaceholderStyle } from './shared';
import { isMonthYearDatesEnabled } from '../../../utils';

export const GoalDateCellRead = ({ row }: { row: GoalInlineRow }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isPlanSavedOnce = useAppSelector(selectIsPlanSavedOnce);
    const dateLabel = (
        <Typography variant="body.large" label={formatGoalDateDisplay(row)} sx={goalsReadTextSx(theme).value} />
    );
    // AEX-3430: a saved goal whose timeline date has elapsed flags the future-date error inline.
    if (rowHasPastGoalDate(row) && !isPlanSavedOnce) {
        return (
            <FieldErrorTooltip message={t('TEXT_VALID_FUTURE_DATE')}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    {dateLabel}
                    <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: '18px' }} />
                </Box>
            </FieldErrorTooltip>
        );
    }
    return dateLabel;
};

const LabeledField = ({ label, children }: { label: string; children: React.ReactNode }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {children}
            <Typography variant="labels.small" label={label} sx={goalsReadTextSx(theme).caption} />
        </Box>
    );
};

const nextPeriodStart = (isAnnual = false) =>
    !isMonthYearDatesEnabled() && isAnnual
        ? dayjs().add(1, 'year').startOf('year').format()
        : dayjs().add(1, 'month').startOf('month').format();

export const GoalDateCellEdit = ({ ctx }: { ctx: GoalRowCtx }) => {
    const { t } = useTranslation();
    const type = ctx.row.type;
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const isTenureLocked = useAppSelector((state) => isGoalTenureLocked(ctx.row, state.adviceGoals));
    const planCreationDate = useAppSelector((state) => state.adviceGoals?.global?.planCreationDate);
    const frequency = ctx.watch('frequency');
    const isAnnual = frequency === 'Annually';
    const isPlanSavedOnce = useAppSelector(selectIsPlanSavedOnce);

    // A saved (tenure-locked) income/wealth goal keeps its term: restrict the categorized target
    // date to the month window that preserves its category so editing it can't move the goal's term.
    // Retirement is always Long and isn't date-categorized, so it never gets a window.
    const lockWindow = type === 'retirement' ? null : getTenureLockWindow(ctx.row, isTenureLocked, planCreationDate);

    if (type === 'wealth') {
        return (
            <InlineDateField
                ctx={ctx}
                name="targetDate"
                required
                isMMYYYY
                startDate={lockWindow?.startDate ?? dayjs().add(1, 'month').startOf('month').format()}
                endDate={lockWindow?.endDate}
            />
        );
    }

    if (type === 'income') {
        const enabled = !!frequency;
        const startDate = nextPeriodStart(isAnnual);
        const incomeStartValue = ctx.watch('incomeStartDate');
        const incomeStart = incomeStartValue ? dayjs(incomeStartValue).format() : startDate;
        const isStartLocked = isPastStartDateLocked(ctx.row.incomeStartDate, isPlanSavedOnce);
        // End-date lower bound is the later of the income start and the tenure-window minimum.
        const endMin =
            lockWindow?.startDate && dayjs(lockWindow.startDate).isAfter(dayjs(incomeStart))
                ? lockWindow.startDate
                : incomeStart;
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <LabeledField label={t('GOALS_INLINE_START')}>
                    <InlineDateField
                        ctx={ctx}
                        name="incomeStartDate"
                        required
                        isMMYYYY={isMonthYearDatesEnabled() || !isAnnual}
                        isYYYY={!isMonthYearDatesEnabled() && isAnnual}
                        enabled={!isStartLocked && enabled}
                        skipValidation={isStartLocked}
                        startDate={startDate}
                    />
                </LabeledField>
                <LabeledField label={t('GOALS_INLINE_END')}>
                    <InlineDateField
                        ctx={ctx}
                        name="targetDate"
                        required
                        isMMYYYY={isMonthYearDatesEnabled() || !isAnnual}
                        isYYYY={!isMonthYearDatesEnabled() && isAnnual}
                        enabled={enabled}
                        startDate={endMin}
                        endDate={lockWindow?.endDate}
                        compareDate={incomeStart}
                        showDateError={!!incomeStartValue}
                        dateErrorMsg={t('END_DATE_AFTER_START_ERROR')}
                    />
                </LabeledField>
            </Box>
        );
    }

    // retirement
    return <RetirementDateEdit ctx={ctx} adviceClient={adviceClient} isAnnual={isAnnual} />;
};

const RetirementDateEdit = ({
    ctx,
    adviceClient,
    isAnnual
}: {
    ctx: GoalRowCtx;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adviceClient: any;
    isAnnual: boolean;
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const frequency = ctx.watch('frequency');
    const option = (ctx.watch('retirementDateOption') as RetirementDateOption) ?? ctx.row.retirementDateOption ?? '';
    const derived = useMemo(() => getRetirementDerivedDates(adviceClient), [adviceClient]);
    const coClientExists = hasCoClient(adviceClient);
    const isPlanSavedOnce = useAppSelector(selectIsPlanSavedOnce);
    const isStartLocked = isPastStartDateLocked(ctx.row.planStartRetirement, isPlanSavedOnce);

    const applyOption = useCallback(
        (opt: RetirementDateOption) => {
            ctx.setValue('retirementDateOption', opt as never, { shouldDirty: true });
            if (opt === 'client') {
                ctx.setValue('planStartRetirement', (derived.client.start ?? null) as never, {
                    shouldValidate: true,
                    shouldDirty: true
                });
                ctx.setValue('targetDate', (derived.client.end ?? null) as never, {
                    shouldValidate: true,
                    shouldDirty: true
                });
                ctx.setValue('planStartRetirementKey', 'retirementAge' as never, { shouldDirty: true });
                ctx.setValue('targetDateKey', 'planningAge' as never, { shouldDirty: true });
            } else if (opt === 'coclient' && derived.coclient) {
                ctx.setValue('planStartRetirement', (derived.coclient.start ?? null) as never, {
                    shouldValidate: true,
                    shouldDirty: true
                });
                ctx.setValue('targetDate', (derived.coclient.end ?? null) as never, {
                    shouldValidate: true,
                    shouldDirty: true
                });
                ctx.setValue('planStartRetirementKey', 'coRetirementAge' as never, { shouldDirty: true });
                ctx.setValue('targetDateKey', 'coPlanningAge' as never, { shouldDirty: true });
            } else {
                // Custom: clear the derived dates but don't validate yet, so the empty required
                // fields don't flag an error before the user has interacted with them.
                ctx.setValue('planStartRetirement', null as never, { shouldValidate: false, shouldDirty: true });
                ctx.setValue('targetDate', null as never, { shouldValidate: false, shouldDirty: true });
                ctx.setValue('planStartRetirementKey', 'custom' as never, { shouldDirty: true });
                ctx.setValue('targetDateKey', 'custom' as never, { shouldDirty: true });
                ctx.clearErrors(['planStartRetirement', 'targetDate'] as never);
            }
        },
        [ctx, derived]
    );

    // Seed derived dates once a non-custom option is chosen and the required frequency is set,
    // if the dates haven't been populated yet. A new row starts unselected (no auto-fill).
    useEffect(() => {
        if (!frequency || option === 'custom' || isStartLocked) return;
        const hasDates = !!ctx.getValues('planStartRetirement') && !!ctx.getValues('targetDate');
        if (!hasDates) applyOption(option);
    }, [frequency, option, applyOption, ctx, isStartLocked]);

    const options = [
        { label: t('GOAL_DATE_CLIENT_RET_TO_PLANNING'), value: 'client' },
        ...(coClientExists ? [{ label: t('GOAL_DATE_COCLIENT_RET_TO_PLANNING'), value: 'coclient' }] : []),
        { label: t('GOAL_DATE_CUSTOM'), value: 'custom' }
    ];

    const enabled = !!frequency;
    const isCustom = option === 'custom';
    const planStartValue = ctx.watch('planStartRetirement');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ComboSelect
                options={options}
                selectedValue={option}
                onSelectChange={(val: string) => applyOption(val as RetirementDateOption)}
                disabled={isStartLocked}
                size="small"
                placeholder={t('GOALS_INLINE_SELECT_GOAL_DATE')}
                placeholderStyle={mutedPlaceholderStyle(theme)}
                data-testid="retirement-date-option"
            />
            <LabeledField label={t('GOALS_INLINE_START')}>
                <InlineDateField
                    ctx={ctx}
                    name="planStartRetirement"
                    required
                    isMMYYYY={isMonthYearDatesEnabled() || !isAnnual}
                    isYYYY={!isMonthYearDatesEnabled() && isAnnual}
                    enabled={!isStartLocked && enabled && isCustom}
                    skipValidation={isStartLocked}
                    startDate={nextPeriodStart(isAnnual)}
                />
            </LabeledField>
            <LabeledField label={t('GOALS_INLINE_END')}>
                <InlineDateField
                    ctx={ctx}
                    name="targetDate"
                    required
                    isMMYYYY={isMonthYearDatesEnabled() || !isAnnual}
                    isYYYY={!isMonthYearDatesEnabled() && isAnnual}
                    enabled={enabled && isCustom}
                    startDate={planStartValue ? dayjs(planStartValue).format() : nextPeriodStart(isAnnual)}
                    showDateError
                />
            </LabeledField>
        </Box>
    );
};
